(ns io.daskul.tutorial.webapp.views.facebook-pages
  "Facebook Messenger connection section (used in Settings).
   OAuth connect flow (no manual token entry) + connection status."
  (:require
   [uix.core :refer [defui $] :as uix]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.tutorial.webapp.domain.facebook-pages.events :as fb.events]))

(def ^:private error-messages
  {"denied"         "Facebook permission was denied. Please try again and grant the required permissions."
   "token-failed"   "Couldn't complete the connection with Facebook. Please try again."
   "no-pages"       "No Facebook Pages found. Make sure you manage at least one Facebook Page."
   "multiple-pages" "You manage more than one Page. Please retry and select a single Page."
   "invalid-state"  "The connection link expired or was invalid. Please try connecting again."
   ;; #276: a Page can only be connected to one school (page_id is globally
   ;; unique). Two codes, because the next step differs: an ACTIVE claim can be
   ;; released self-serve by disconnecting it there, while an INACTIVE one
   ;; (someone disconnected, which soft-deactivates and keeps the claim) can't
   ;; be released from the UI at all. Neither message names the other school.
   "page-connected-elsewhere"
   "This Facebook Page is already connected to another school. Disconnect it there first, then try again."
   "page-claimed-elsewhere"
   "This Facebook Page was previously connected to another school and is still reserved. Please contact support to release it."
   "internal"       "Something went wrong connecting to Facebook. Please try again."})

(defn- query-param [k]
  (some-> js/window.location.search
          (js/URLSearchParams.)
          (.get k)))

(defn- facebook-icon
  "Official Facebook brand mark (single-path f-in-circle)."
  [{:keys [size color]}]
  ($ :svg {:width (or size 24) :height (or size 24) :viewBox "0 0 24 24"
           :fill (or color "#1877F2") :aria-hidden "true"}
    ($ :path {:d "M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"})))

(defui facebook-section
  "Facebook Messenger connection — header + connect / connected status.
   No outer card; the caller (Settings) supplies the section card."
  []
  (let [pages       (urf/use-subscribe [::agg.subs/all :facebook-page])
        loading?    (urf/use-subscribe [::agg.subs/loading? :facebook-page])
        connecting? (urf/use-subscribe [::fb.events/connecting?])
        disconnecting? (urf/use-subscribe [::fb.events/disconnecting?])
        {:keys [status page]} (fb.events/connection-status pages)
        fb-error        (query-param "facebook-error")
        just-connected? (= "connected" (query-param "facebook"))]
    (uix/use-effect
     (fn []
       (rf/dispatch [::agg.events/list :facebook-page {}])
       js/undefined)
     [])
    ($ ui/stack {:gap "lg"}
      ($ ui/group {:gap "md" :wrap "nowrap"}
        (facebook-icon {:size 44})
        ($ ui/stack {:gap 2}
          ($ ui/title {:order 4} "Facebook Messenger")
          ($ ui/text {:size "sm" :c "dimmed"}
            "Receive and reply to Messenger messages from your Facebook Page.")))

      ;; Post-redirect feedback (?facebook=connected / ?facebook-error=…)
      (when fb-error
        ($ ui/alert {:color "red" :title "Connection failed" :radius "md"}
          (get error-messages fb-error "Couldn't connect to Facebook. Please try again.")))
      (when (and just-connected? (= status :connected))
        ($ ui/alert {:color "green" :radius "md"}
          "Your Facebook Page is now connected."))

      ($ ui/divider)

      (cond
        loading?
        ($ ui/text {:size "sm" :c "dimmed"} "Loading…")

        (= status :connected)
        ($ ui/stack {:gap "md"}
          ($ ui/paper {:withBorder true :p "md" :radius "md"}
            ($ ui/group {:justify "space-between" :wrap "nowrap"}
              ($ ui/group {:gap "sm" :wrap "nowrap"}
                ($ ui/badge {:color "green" :variant "light"} "Connected")
                ($ ui/text {:fw 600} (or (:name page) (:page-id page))))
              ($ ui/button {:variant "default" :size "xs"
                            :loading disconnecting?
                            :onClick #(rf/dispatch [::fb.events/disconnect])}
                "Disconnect")))
          ($ ui/text {:size "sm" :c "dimmed"}
            "Messenger messages to this Page flow into your Inbox, where you can reply."))

        :else
        ($ ui/stack {:gap "sm" :align "flex-start"}
          ($ ui/button {:size "md"
                        :loading connecting?
                        :leftSection (facebook-icon {:size 18 :color "#fff"})
                        :styles {:root {:backgroundColor "#1877F2" :color "#fff"}}
                        :onClick #(rf/dispatch [::fb.events/start-connect])}
            "Continue with Facebook")
          ($ ui/text {:size "xs" :c "dimmed"}
            "You'll be redirected to Facebook to grant messaging permissions."))))))
